import type { CSSProperties, ReactNode } from "react";
import React from "react";

import { InteractiveSurface, type T_KitCssVars } from "./InteractiveSurface";
import "./kit.css";

export type T_ButtonVariant = "danger" | "primary" | "quiet" | "secondary";
export type T_ButtonSize = "compact" | "regular";

export type T_ButtonProps = {
  ariaLabel?: string;
  disabled?: boolean;
  label: string;
  leading?: ReactNode;
  loading?: boolean;
  onClick: () => void;
  size?: T_ButtonSize;
  style?: CSSProperties;
  trailing?: ReactNode;
  variant?: T_ButtonVariant;
};

type T_ButtonPalette = {
  background: string;
  foreground: string;
  pressedBackground: string;
};

function getButtonPalette(variant: T_ButtonVariant): T_ButtonPalette {
  switch (variant) {
    case "danger":
      return {
        background: "var(--lattice-dark-grey-dark-7)",
        foreground: "var(--lattice-palette-error-400)",
        pressedBackground: "var(--lattice-dark-grey-dark-6)",
      };
    case "primary":
      return {
        background: "var(--lattice-palette-white-pure)",
        foreground: "var(--lattice-dark-grey-dark-8)",
        pressedBackground: "var(--lattice-palette-neutral-200)",
      };
    case "quiet":
      return {
        background: "transparent",
        foreground: "var(--lattice-palette-text-secondary)",
        pressedBackground: "var(--lattice-dark-grey-dark-6)",
      };
    case "secondary":
      return {
        background: "var(--lattice-dark-grey-dark-6)",
        foreground: "var(--lattice-palette-white-pure)",
        pressedBackground: "var(--lattice-dark-grey-dark-4)",
      };
  }
}

/**
 * Canonical text button — web mirror of the mobile Button. Visual hierarchy
 * comes from fills, never borders.
 */
export function Button({
  ariaLabel,
  disabled = false,
  label,
  leading,
  loading = false,
  onClick,
  size = "regular",
  style,
  trailing,
  variant = "primary",
}: T_ButtonProps) {
  const palette = getButtonPalette(variant);
  const isDisabled = disabled || loading;
  const fgVar: T_KitCssVars = { "--lk-fg": palette.foreground };

  return (
    <InteractiveSurface
      ariaBusy={loading || undefined}
      ariaLabel={ariaLabel ?? label}
      backgroundColor={palette.background}
      className={[
        "lk-button",
        size === "compact" ? "lk-button--compact" : "lk-button--regular",
        isDisabled ? "lk-button--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={isDisabled}
      onClick={onClick}
      pressedBackgroundColor={palette.pressedBackground}
      style={{ ...fgVar, color: palette.foreground, ...style }}
    >
      {loading ? (
        <span aria-hidden className="lk-spinner" />
      ) : (
        <>
          {leading}
          <span className="lk-button__label">{label}</span>
          {trailing}
        </>
      )}
    </InteractiveSurface>
  );
}
